import {Domain, DomainVerification, LoginPolicy} from '@/api/apolloPlatform/loginPolicy';

export const useLoginPolicyStore = defineStore('loginPolicy', () => {
  const domains = ref<Domain[]>([]);
  const domainsLoading = ref(false);
  const domainsLoaded = ref(false);

  const domainVerifications = ref<DomainVerification[]>([]);
  const domainVerificationsLoading = ref(false);
  const domainVerificationsLoaded = ref(false);

  async function loadDomains() {
    if (domainsLoading.value) return;
    try {
      domainsLoading.value = true;
      domains.value = await fetchDomains();
      domainsLoaded.value = true;
    } finally {
      domainsLoading.value = false;
    }
  }

  function ensureDomainsLoaded() {
    if (!domainsLoaded.value) {
      loadDomains();
    }
  }

  async function loadDomainVerifications() {
    if (domainVerificationsLoading.value) return;
    try {
      domainVerificationsLoading.value = true;
      domainVerifications.value = await fetchDomainVerifications();
      domainVerificationsLoaded.value = true;
    } finally {
      domainVerificationsLoading.value = false;
    }
  }

  function ensureDomainVerificationsLoaded() {
    if (!domainVerificationsLoaded.value) {
      loadDomainVerifications();
    }
  }

  async function removeDomain(id: string) {
    await deleteDomain(id);
    domains.value = domains.value.filter((d) => d.id !== id);
  }

  async function removeDomainVerification(dvId: string) {
    await deleteDomainVerification(dvId);
    domainVerifications.value = domainVerifications.value.filter((d) => d.id !== dvId);
  }

  async function claimDomain(dvId: string, verificationCode: string) {
    await postClaimDomain(dvId, verificationCode);
    domainVerifications.value = domainVerifications.value.filter((d) => d.id !== dvId);
    loadDomains();
  }

  async function startDomainVerification(domain: string, email: string) {
    const newDv = await createDomainVerification({domain: domain, sentTo: email});
    domainVerifications.value.push(newDv);
  }

  const loginPolicies = ref<LoginPolicy[]>([]);
  const loginPoliciesLoading = ref(false);
  const loginPoliciesLoaded = ref(false);

  async function loadLoginPolicies() {
    if (loginPoliciesLoading.value) return;
    try {
      loginPoliciesLoading.value = true;
      loginPolicies.value = await fetchLoginPolicies();
      loginPoliciesLoaded.value = true;
    } finally {
      loginPoliciesLoading.value = false;
    }
  }

  async function ensureLoginPoliciesLoaded() {
    if (!loginPoliciesLoaded.value) loadLoginPolicies();
  }

  function loadedPolicyById(id: string): LoginPolicy | undefined {
    return loginPolicies.value.filter((lp) => lp.id === id)[0];
  }

  function loadedActivePolicy(): LoginPolicy | undefined {
    return loginPolicies.value.filter((lp) => lp.active)[0];
  }

  async function activatePolicy(toActivate: LoginPolicy, oldPolicy?: LoginPolicy) {
    await postActivateLoginPolicy(toActivate.id, oldPolicy?.id);
    loginPolicies.value = loginPolicies.value.map((p) => ({
      ...p,
      active: p.id === toActivate.id,
      frozen: p.frozen || p.id === toActivate.id || p.id === oldPolicy?.id,
    }));
  }

  async function deactivatePolicy(toDeactivate: LoginPolicy) {
    await postDeactivateLoginPolicy(toDeactivate.id);
    loginPolicies.value = loginPolicies.value.map((p) => ({
      ...p,
      active: p.active && p.id !== toDeactivate.id,
      frozen: p.frozen || p.id === toDeactivate.id,
    }));
  }

  async function createPolicyFromScratch(): Promise<LoginPolicy> {
    const newPolicy = await postCreateNewLoginPolicy();
    loginPolicies.value.unshift(newPolicy);
    return newPolicy;
  }
  async function createPolicyFromExisting(existingId: string): Promise<LoginPolicy> {
    const newPolicy = await postCreateLoginPolicyFromExisting(existingId);
    loginPolicies.value.unshift(newPolicy);
    return newPolicy;
  }

  async function editPolicy(id: string, patch: PatchLoginPolicy) {
    const updatedPolicy = await patchLoginPolicy(id, patch);
    loginPolicies.value = loginPolicies.value.map((p) =>
      p.id === updatedPolicy.id ? updatedPolicy : p,
    );
  }

  async function removeLoginPolicy(id: string) {
    await deleteLoginPolicy(id);
    loginPolicies.value = loginPolicies.value.filter((lp) => lp.id !== id);
  }

  return {
    ensureDomainsLoaded,
    loadDomains,
    domains,
    domainsLoaded,
    domainsLoading,
    ensureDomainVerificationsLoaded,
    loadDomainVerifications,
    domainVerifications,
    domainVerificationsLoaded,
    domainVerificationsLoading,
    removeDomain,
    removeDomainVerification,
    claimDomain,
    startDomainVerification,
    loginPolicies,
    loginPoliciesLoading,
    loginPoliciesLoaded,
    loadLoginPolicies,
    ensureLoginPoliciesLoaded,
    loadedPolicyById,
    loadedActivePolicy,
    activatePolicy,
    deactivatePolicy,
    editPolicy,
    removeLoginPolicy,
    createPolicyFromScratch,
    createPolicyFromExisting,
  };
});
